import { InputAdornment, TextField } from "@mui/material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";

interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    value: string;
    disabled?: boolean;
}

const PhoneMaskInput = React.forwardRef<HTMLInputElement, CustomProps>(
    function PhoneMaskInput(props, ref) {
        const { onChange, onBlur, value, disabled, ...other } = props;

        // Clean the incoming value and ensure it's properly formatted for the mask
        const cleanedValue = value ? value.replace(/\D/g, "") : "";

        return (
            <IMaskInput
                {...other}
                mask="(000) 000-0000"
                definitions={{
                    "0": /[0-9]/,
                }}
                inputRef={ref}
                value={cleanedValue}
                disabled={disabled}
                unmask={false}
                onAccept={(maskedValue: string) => {
                    onChange({ target: { name: props.name, value: maskedValue } });
                }}
                onBlur={onBlur}
                placeholder="_"
                lazy={false} // Show mask immediately
            />
        );
    },
);

type Props = {
    name: string;
    label: string;
    required?: boolean;
    disabled?: boolean;
    sx?: object;
    placeholder?: string;
    helperText?: string;
    startAdornment?: React.ReactNode;
};

const RhfMaskedPhoneField = ({
    name,
    label,
    required = false,
    disabled = false,
    sx = {},
    placeholder = "(___) ___-____",
    helperText = "",
    startAdornment,
    ...rest
}: Props) => {
    const form = useFormContext();

    return (
        <Controller
            control={form.control}
            name={name}
            render={({ field, fieldState: { error } }) => {
                // Helper function to format any input to (000) 000-0000 format
                const formatPhoneNumber = (input: string): string => {
                    if (!input) {
                        return "";
                    }

                    // Extract only digits
                    const digits = input.replace(/\D/g, "");

                    // Format to (000) 000-0000 if we have enough digits
                    if (digits.length >= 10) {
                        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
                            6,
                            10,
                        )}`;
                    }

                    // Return partial formatting for incomplete numbers
                    if (digits.length >= 6) {
                        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                    }
                    if (digits.length >= 3) {
                        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    }
                    if (digits.length > 0) {
                        return `(${digits}`;
                    }

                    return "";
                };

                // Format the current value for display and ensure it's properly formatted for storage
                const formattedValue = formatPhoneNumber(field.value || "");

                return (
                    <TextField
                        label={label}
                        error={!!error}
                        helperText={error ? error.message : helperText}
                        fullWidth
                        required={required}
                        disabled={disabled}
                        placeholder={placeholder}
                        sx={sx}
                        slotProps={{
                            input: {
                                inputComponent: PhoneMaskInput as any,
                                inputProps: {
                                    name: field.name,
                                    value: formattedValue,
                                    onChange: field.onChange,
                                    onBlur: field.onBlur,
                                    disabled: disabled,
                                },
                                startAdornment: startAdornment ? (
                                    <InputAdornment position="start">
                                        {startAdornment}
                                    </InputAdornment>
                                ) : undefined,
                            },
                        }}
                        {...rest}
                    />
                );
            }}
        />
    );
};

export default RhfMaskedPhoneField;
